/**
 * IndexedDB File Storage Manager
 * 
 * Single source of truth for all code file storage and management.
 * Replaces localStorage-based fileStorageManager for better scalability and performance.
 * 
 * Usage:
 * - Direct: import indexedDbFileStorage from './utils/indexedDbFileStorage'
 * - Unified: import storageManager from './utils/storageManager' (recommended)
 * 
 * Features:
 * - All file operations use IndexedDB
 * - No localStorage dependency
 * - Better quota handling (50MB+ vs 5MB)
 * - Atomic transactions
 * - Efficient querying with indexes
 * - State tracking (modified, sent, modified_after_sent)
 */

import { DEFAULT_MICROSERVICE_BRANCH, DEFAULT_PROJECT_BRANCH } from '../constants/gitConstants';
import commitMessageStorage from './commitMessageStorage';

const DB_NAME = 'CodeFileStorage';
const DB_VERSION = 2;
const FILES_STORE = 'files';
const METADATA_STORE = 'metadata';

// Git lifecycle states (tracking relationship with GitLab)
const GIT_STATE = {
  ORIGINAL: 'original',    // File from GitLab, unmodified
  MODIFIED: 'modified',    // File has local changes not pushed to GitLab
  PUSHED: 'pushed',        // File has been pushed to GitLab
  NEW: 'new',             // New file not in GitLab yet
  DELETED: 'deleted'       // File marked for deletion
};

// Preview lifecycle states (tracking relationship with live preview)
const PREVIEW_STATE = {
  NOT_SENT: 'not_sent',    // File not sent to preview yet
  SENT: 'sent',            // File sent to preview and up-to-date
  OUTDATED: 'outdated'     // File sent to preview but modified since
};


/**
 * Files to EXCLUDE from being sent to AI - dependency lock files and generated files
 * that are not useful for coding agents
 */
const EXCLUDED_FILES_FOR_AI = [
  // JavaScript/Node.js
  'package-lock.json',
  'yarn.lock',
  'pnpm-lock.yaml',
  'npm-shrinkwrap.json',
  
  // Python
  'poetry.lock',
  'Pipfile.lock',
  'pdm.lock',
  'requirements.lock',
  
  // Ruby
  'Gemfile.lock',
  
  // PHP
  'composer.lock',
  
  // Rust
  'Cargo.lock',
  
  // Go
  'go.sum',
  
  // .NET
  'packages.lock.json',
  'paket.lock',
  
  // Java/JVM
  'gradle.lockfile',
  'gradlew',
  'gradlew.bat',
  'mvnw',
  'mvnw.cmd',
  
  // Swift
  'Package.resolved',
  
  // Dart/Flutter  
  'pubspec.lock',
  
  // Other generated/build files
  '.DS_Store',
  'Thumbs.db',
  '.env.local',
  '.env.*.local'
];

/**
 * Check if a file should be excluded from AI context
 * @param {string} filePath - Full file path
 * @returns {boolean} - True if file should be excluded
 */
function shouldExcludeFromAI(filePath) {
  const fileName = filePath.split('/').pop();
  return EXCLUDED_FILES_FOR_AI.includes(fileName);
}

/**
 * Get current tenant ID from localStorage
 * @returns {string|null}
 */
function getTenantId() {
  try {
    const stored = localStorage.getItem('mia_selectedTenant');
    if (stored) {
      const tenant = JSON.parse(stored);
      return tenant.tenantId || tenant.id || null;
    }
  } catch (error) {
    console.error('Failed to get tenant ID:', error); /* eslint-disable-line no-console */
  }
  return null;
}

class IndexedDbFileStorage {
  constructor() {
    this.db = null;
    this.isSupported = typeof indexedDB !== 'undefined';
    this.initPromise = null;
  }

  /**
   * Initialize the database connection with proper schema
   * @returns {Promise<IDBDatabase>}
   */
  async init() {
    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    if (!this.isSupported) {
      throw new Error('IndexedDB is not supported in this browser');
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ Failed to open IndexedDB:', request.error); /* eslint-disable-line no-console */
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB file storage initialized'); /* eslint-disable-line no-console */
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create files store with indexes
        if (!db.objectStoreNames.contains(FILES_STORE)) {
          const filesStore = db.createObjectStore(FILES_STORE, {keyPath: 'id'});
          
          // Indexes for efficient querying
          filesStore.createIndex('projectId', 'projectId', {unique: false});
          filesStore.createIndex('serviceName', 'serviceName', {unique: false});
          filesStore.createIndex('filePath', 'filePath', {unique: false});
          filesStore.createIndex('state', 'state', {unique: false});
          filesStore.createIndex('lastModified', 'lastModified', {unique: false});
          filesStore.createIndex('projectService', ['projectId', 'serviceName'], {unique: false});
          filesStore.createIndex('fullPath', ['projectId', 'serviceName', 'filePath'], {unique: true});
          
          console.log('📦 Created files object store with indexes'); /* eslint-disable-line no-console */
        }

        // Create metadata store for project-level data
        if (!db.objectStoreNames.contains(METADATA_STORE)) {
          const metadataStore = db.createObjectStore(METADATA_STORE, {keyPath: 'key'});
          metadataStore.createIndex('projectId', 'projectId', {unique: false});
          console.log('📦 Created metadata object store'); /* eslint-disable-line no-console */
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Generate unique ID for file storage
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {string}
   */
  generateFileId(projectId, serviceName, filePath) {
    return `${projectId}::${serviceName}::${filePath}`;
  }


  /**
   * Save file content to IndexedDB
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @param {string} content
   * @param {object} options - Additional options (branch, originalContent, etc.)
   * @returns {Promise<boolean>}
   */
  async saveFile(projectId, serviceName, filePath, content, options = {}) {
    try {
      await this.init();

      const id = this.generateFileId(projectId, serviceName, filePath);
      
      // Get current file to determine state transition
      const currentFile = await this.getFile(projectId, serviceName, filePath);
      
      // Determine git and preview states separately
      let gitState, previewState;
      
      // Check if file is being pulled from Git - should always be ORIGINAL
      if (options.fromGit) {
        gitState = GIT_STATE.ORIGINAL;
        previewState = PREVIEW_STATE.NOT_SENT;
      } else if (currentFile) {
        // File exists - check if content changed
        const contentChanged = currentFile.content !== content;
        
        if (!contentChanged) {
          // Content hasn't changed - keep current states
          gitState = currentFile.gitState || this._migrateToGitState(currentFile.state);
          previewState = currentFile.previewState || this._migrateToPreviewState(currentFile.state);
        } else {
          // Content changed - update states
          
          // Git state: if file was pushed/original, mark as modified; keep NEW as NEW
          const currentGitState = currentFile.gitState || this._migrateToGitState(currentFile.state);
          if (currentGitState === GIT_STATE.PUSHED || currentGitState === GIT_STATE.ORIGINAL) {
            gitState = GIT_STATE.MODIFIED;
          } else if (currentGitState === GIT_STATE.NEW) {
            gitState = GIT_STATE.NEW;
          } else {
            gitState = GIT_STATE.MODIFIED;
          }
          
          // Preview state: if file was sent to preview, mark as outdated
          const currentPreviewState = currentFile.previewState || this._migrateToPreviewState(currentFile.state);
          if (currentPreviewState === PREVIEW_STATE.SENT) {
            previewState = PREVIEW_STATE.OUTDATED;
          } else {
            previewState = currentPreviewState;
          }
        }
      } else if (options.isNew) {
        gitState = GIT_STATE.NEW;
        previewState = PREVIEW_STATE.NOT_SENT;
      } else {
        // New file from GitLab - mark as ORIGINAL
        gitState = GIT_STATE.ORIGINAL;
        previewState = PREVIEW_STATE.NOT_SENT;
      }

      const fileData = {
        id,
        projectId,
        serviceName,
        filePath,
        content,
        gitState,
        previewState,
        lastModified: Date.now(),
        lastPushedToGitLab: currentFile?.lastPushedToGitLab || null,
        lastSentToPreview: currentFile?.lastSentToPreview || null,
        originalContent: options.originalContent || currentFile?.originalContent || content,
        branch: options.branch || currentFile?.branch || DEFAULT_MICROSERVICE_BRANCH,
        fileName: options.fileName || filePath.split('/').pop(),
        language: options.language || currentFile?.language,
        isBinary: options.isBinary || currentFile?.isBinary || false,
        mimeType: options.mimeType || currentFile?.mimeType,
        size: options.size || currentFile?.size,
        metadata: {
          ...currentFile?.metadata,
          ...options.metadata
        }
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.put(fileData);

        request.onsuccess = async () => {
          // Check if content actually changed before dispatching event
          const contentChanged = !currentFile || currentFile.content !== content;
          
          // eslint-disable-next-line no-console
          console.log(`💾 Saved file to IndexedDB: ${filePath} (state: ${gitState}, contentChanged: ${contentChanged})`);
          
          // Track commit message for file modifications
          if (contentChanged && gitState !== GIT_STATE.ORIGINAL) {
            try {
              const tenantId = getTenantId();
              const fileName = filePath.split('/').pop();
              const commitMessage = `Modified ${fileName}`;
              
              await commitMessageStorage.appendMessage(
                tenantId,
                projectId,
                serviceName,
                commitMessage
              );
              
              // eslint-disable-next-line no-console
              console.log(`📝 Appended commit message: ${commitMessage}`);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Failed to append commit message:', error);
              // Don't fail the file save if commit message tracking fails
            }
          }
          
          // Only dispatch event if content actually changed
          // This prevents infinite loops where the same content is saved repeatedly
          if (contentChanged) {
            // Dispatch event to notify Monaco editor and tree view of changes
            const event = new CustomEvent('monacoFileChanged', {
              detail: {
                projectId,
                serviceName,
                filePath,
                content,
                state: gitState
              }
            });
            window.dispatchEvent(event);
          }
          
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to save file:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error saving file:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Get file content from IndexedDB
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<object|null>}
   */
  async getFile(projectId, serviceName, filePath) {
    try {
      await this.init();

      const id = this.generateFileId(projectId, serviceName, filePath);

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readonly');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.get(id);

        request.onsuccess = () => {
          resolve(request.result || null);
        };

        request.onerror = () => {
          console.error('❌ Failed to get file:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting file:', error); /* eslint-disable-line no-console */
      return null;
    }
  }

  /**
   * Get all files for a project/service
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<Array>}
   */
  async getAllFiles(projectId, serviceName) {
    try {
      await this.init();

      // Validate parameters to prevent IndexedDB key errors
      // Check for undefined, null, empty string, or non-string types
      if (!projectId || !serviceName || typeof projectId !== 'string' || typeof serviceName !== 'string' || projectId.trim() === '' || serviceName.trim() === '') {
        console.warn('⚠️ getAllFiles called with invalid parameters:', { projectId, serviceName }); /* eslint-disable-line no-console */
        return [];
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readonly');
        const store = transaction.objectStore(FILES_STORE);
        const index = store.index('projectService');
        const request = index.getAll([projectId, serviceName]);

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          console.error('❌ Failed to get all files:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting all files:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Get modified files (files that need to be pushed to GitLab)
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<Array>}
   */
  async getModifiedFiles(projectId, serviceName) {
    try {
      const allFiles = await this.getAllFiles(projectId, serviceName);
      const modifiedFiles = allFiles.filter(file => {
        const gitState = file.gitState || this._migrateToGitState(file.state);
        return gitState === GIT_STATE.MODIFIED || 
               gitState === GIT_STATE.NEW || 
               gitState === GIT_STATE.DELETED;
      });
      
      // Debug logging
      // eslint-disable-next-line no-console
      console.log(`🔍 getModifiedFiles(${projectId}, ${serviceName}):`, {
        totalFiles: allFiles.length,
        modifiedCount: modifiedFiles.length,
        allFileStates: allFiles.map(f => ({
          path: f.filePath, 
          gitState: f.gitState || this._migrateToGitState(f.state),
          previewState: f.previewState
        })),
        modifiedFileStates: modifiedFiles.map(f => ({
          path: f.filePath, 
          gitState: f.gitState || this._migrateToGitState(f.state)
        }))
      });
      
      return modifiedFiles;
    } catch (error) {
      console.error('Error getting modified files:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Get deleted files
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<Array>}
   */
  async getDeletedFiles(projectId, serviceName) {
    try {
      const allFiles = await this.getAllFiles(projectId, serviceName);
      return allFiles.filter(file => {
        const gitState = file.gitState || this._migrateToGitState(file.state);
        return gitState === GIT_STATE.DELETED;
      });
    } catch (error) {
      console.error('Error getting deleted files:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Get changed files for preview (files that need to be synced to live preview)
   * Returns files that haven't been sent to preview yet or are outdated
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<Array>}
   */
  async getChangedFilesForPreview(projectId, serviceName) {
    try {
      const allFiles = await this.getAllFiles(projectId, serviceName);
      // Return files that need preview sync: NOT_SENT or OUTDATED
      return allFiles.filter(file => {
        const previewState = file.previewState || this._migrateToPreviewState(file.state);
        return previewState === PREVIEW_STATE.NOT_SENT || 
               previewState === PREVIEW_STATE.OUTDATED;
      });
    } catch (error) {
      console.error('Error getting changed files for preview:', error); /* eslint-disable-line no-console */
      return [];
    }
  }

  /**
   * Mark file as sent to preview
   * Updates previewState to SENT without affecting gitState
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async markAsPreviewSent(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      if (!file) return false;

      // Update preview state to SENT
      file.previewState = PREVIEW_STATE.SENT;
      file.lastSentToPreview = Date.now();
      
      // Keep gitState unchanged - preview sync is independent of git lifecycle

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.put(file);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          console.log(`✅ Marked as sent to preview: ${filePath}`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to mark as preview sent:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error marking as preview sent:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Mark file as sent to GitLab (pushed)
   * Updates gitState to PUSHED without affecting previewState
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async markAsSent(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      if (!file) return false;

      // Update git state to PUSHED
      file.gitState = GIT_STATE.PUSHED;
      file.lastPushedToGitLab = Date.now();
      
      // Keep previewState unchanged - git push is independent of preview lifecycle

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.put(file);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          console.log(`✅ Marked as sent to GitLab: ${filePath}`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to mark as sent:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error marking as sent:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Mark all changed files as sent
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<void>}
   */
  async markAllChangedAsPreviewSent(projectId, serviceName) {
    try {
      const changedFiles = await this.getChangedFilesForPreview(projectId, serviceName);
      await Promise.all(
        changedFiles.map(file => this.markAsPreviewSent(projectId, serviceName, file.filePath))
      );
    } catch (error) {
      console.error('Error marking all as preview sent:', error); /* eslint-disable-line no-console */
    }
  }

  async markAllChangedAsSent(projectId, serviceName) {
    try {
      const changedFiles = await this.getChangedFilesForPreview(projectId, serviceName);
      await Promise.all(
        changedFiles.map(file => this.markAsSent(projectId, serviceName, file.filePath))
      );
    } catch (error) {
      console.error('Error marking all as sent:', error); /* eslint-disable-line no-console */
    }
  }

  /**
   * Mark file as deleted
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async markAsDeleted(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      if (!file) return false;

      file.gitState = GIT_STATE.DELETED;
      file.lastModified = Date.now();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.put(file);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          console.log(`🗑️ Marked as deleted: ${filePath}`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to mark as deleted:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error marking as deleted:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Delete file from IndexedDB
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async deleteFile(projectId, serviceName, filePath, options = {}) {
    try {
      await this.init();

      const id = this.generateFileId(projectId, serviceName, filePath);

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([FILES_STORE], 'readwrite');
        const store = transaction.objectStore(FILES_STORE);
        const request = store.delete(id);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          console.log(`🗑️ Deleted file from IndexedDB: ${filePath}`);
          
          // Only dispatch event if not suppressed (e.g., during git pull)
          if (!options.suppressEvent) {
            // Dispatch event to notify Monaco editor and tree view of deletion
            const event = new CustomEvent('monacoFileChanged', {
              detail: {
                projectId,
                serviceName,
                filePath,
                deleted: true
              }
            });
            window.dispatchEvent(event);
          }
          
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to delete file:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error deleting file:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Clear all files for a project/service
   * @param {string} projectId
   * @param {string} serviceName
   * @param {object} options - Options for clearing files
   * @returns {Promise<void>}
   */
  async clearProjectFiles(projectId, serviceName, options = {}) {
    try {
      const files = await this.getAllFiles(projectId, serviceName);
      await Promise.all(
        files.map(file => this.deleteFile(projectId, serviceName, file.filePath, options))
      );
      // eslint-disable-next-line no-console
      console.log(`🗑️ Cleared all files for ${projectId}/${serviceName}`);
    } catch (error) {
      console.error('Error clearing project files:', error); /* eslint-disable-line no-console */
    }
  }

  /**
   * Check if there are any modified files for a project/service
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<boolean>}
   */
  async hasModifiedFiles(projectId, serviceName) {
    try {
      const modifiedFiles = await this.getModifiedFiles(projectId, serviceName);
      return modifiedFiles.length > 0;
    } catch (error) {
      console.error('Error checking for modified files:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Get count of modified files for a project/service
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<number>}
   */
  async getModifiedFilesCount(projectId, serviceName) {
    try {
      const modifiedFiles = await this.getModifiedFiles(projectId, serviceName);
      return modifiedFiles.length;
    } catch (error) {
      console.error('Error getting modified files count:', error); /* eslint-disable-line no-console */
      return 0;
    }
  }

  /**
   * Check if file exists
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async fileExists(projectId, serviceName, filePath) {
    const file = await this.getFile(projectId, serviceName, filePath);
    return !!file;
  }

  /**
   * Get file state
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<string|null>}
   */
  async getFileState(projectId, serviceName, filePath) {
    const file = await this.getFile(projectId, serviceName, filePath);
    // Return a state object with both git and preview states
    if (!file) return null;

    return {
      gitState: file.gitState || GIT_STATE.MODIFIED,
      previewState: file.previewState || PREVIEW_STATE.NOT_SENT
    };
  }

  /**
   * Check if a file has local modifications
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async isFileModified(projectId, serviceName, filePath) {
    const file = await this.getFile(projectId, serviceName, filePath);
    if (!file) return false;
    
    const gitState = file.gitState || this._migrateToGitState(file.state);
    return gitState === GIT_STATE.MODIFIED || gitState === GIT_STATE.NEW;
  }

  /**
   * Get all files for preview (merges original with modified)
   * @param {string} projectId
   * @param {string} serviceName
   * @param {object} originalFiles - Original files object {filePath: content}
   * @returns {Promise<object>} Combined files object {filePath: content}
   */
  async getAllFilesForPreview(projectId, serviceName, originalFiles = {}) {
    const allStoredFiles = await this.getAllFiles(projectId, serviceName);
    const allFiles = {...originalFiles};
    
    // Override with stored versions (prioritize modified, new, or different content)
    for (const fileData of allStoredFiles) {
      const gitState = fileData.gitState || this._migrateToGitState(fileData.state);
      if (gitState !== GIT_STATE.DELETED) {
        allFiles[fileData.filePath] = fileData.content;
      }
    }
    
    return allFiles;
  }

  /**
   * Save metadata (for project-level info)
   * @param {string} key
   * @param {any} value
   * @param {string} projectId
   * @returns {Promise<boolean>}
   */
  async saveMetadata(key, value, projectId = null) {
    try {
      await this.init();

      const data = {
        key,
        value,
        projectId,
        lastModified: Date.now()
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([METADATA_STORE], 'readwrite');
        const store = transaction.objectStore(METADATA_STORE);
        const request = store.put(data);

        request.onsuccess = () => {
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to save metadata:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error saving metadata:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Get metadata
   * @param {string} key
   * @returns {Promise<any|null>}
   */
  async getMetadata(key) {
    try {
      await this.init();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([METADATA_STORE], 'readonly');
        const store = transaction.objectStore(METADATA_STORE);
        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result?.value || null);
        };

        request.onerror = () => {
          console.error('❌ Failed to get metadata:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting metadata:', error); /* eslint-disable-line no-console */
      return null;
    }
  }

  /**
   * Get all files with their content for AI context
   * Merges original files from git with modified files from IndexedDB
   * @param {string} projectId
   * @param {string} serviceName
   * @param {object} originalFiles - Original files from git {filePath: content}
   * @returns {Promise<object>} Combined files object {filePath: content}
   */
  async getFilesForAIContext(projectId, serviceName, originalFiles = {}) {
    try {
      const modifiedFiles = await this.getAllFiles(projectId, serviceName);
      const allFiles = {...originalFiles};
      
      // Override with modified versions from IndexedDB
      modifiedFiles.forEach(file => {
        allFiles[file.filePath] = file.content;
      });
      
      return allFiles;
    } catch (error) {
      console.error('Error getting files for AI context:', error); /* eslint-disable-line no-console */
      return originalFiles || {};
    }
  }

  /**
   * Migrate from localStorage fileStorageManager
   * @param {string} projectId
   * @param {string} serviceName
   * @returns {Promise<number>} Number of files migrated
   */
  async migrateFromLocalStorage(projectId, serviceName) {
    try {
      const prefix = `monaco_file_${projectId}/${serviceName}/`;
      let migratedCount = 0;

      // Get all matching keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            const filePath = key.substring(prefix.length);

            await this.saveFile(
              projectId,
              serviceName,
              filePath,
              data.content,
              {
                originalContent: data.originalContent,
                branch: data.branch,
                metadata: data
              }
            );

            // Set correct state based on legacy data
            const file = await this.getFile(projectId, serviceName, filePath);
            if (file) {
              const legacyState = data.state || 'modified';
              // Migrate legacy state strings to new system
              switch (legacyState) {
                case 'original':
                  file.gitState = GIT_STATE.ORIGINAL;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
                case 'new':
                  file.gitState = GIT_STATE.NEW;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
                case 'deleted':
                  file.gitState = GIT_STATE.DELETED;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
                case 'sent':
                  file.gitState = GIT_STATE.PUSHED;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
                case 'preview_sent':
                  file.gitState = GIT_STATE.MODIFIED;
                  file.previewState = PREVIEW_STATE.SENT;
                  break;
                case 'modified_after_sent':
                  file.gitState = GIT_STATE.PUSHED;
                  file.previewState = PREVIEW_STATE.OUTDATED;
                  break;
                case 'modified':
                default:
                  file.gitState = GIT_STATE.MODIFIED;
                  file.previewState = PREVIEW_STATE.NOT_SENT;
                  break;
              }
              await this.db.transaction([FILES_STORE], 'readwrite')
                .objectStore(FILES_STORE)
                .put(file);
            }

            localStorage.removeItem(key);
            migratedCount++;
          } catch (err) {
            console.error(`Failed to migrate ${key}:`, err); /* eslint-disable-line no-console */
          }
        }
      }

      if (migratedCount > 0) {
        // eslint-disable-next-line no-console
        console.log(`✅ Migrated ${migratedCount} files from localStorage to IndexedDB`);
      }

      return migratedCount;
    } catch (error) {
      console.error('Error migrating from localStorage:', error); /* eslint-disable-line no-console */
      return 0;
    }
  }

  /**
   * Helper: Convert base64 string to ArrayBuffer (for binary files)
   * @param {string} base64 - Base64 encoded string
   * @returns {ArrayBuffer}
   */
  base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Helper: Convert base64 string to Blob (for binary files)
   * @param {string} base64 - Base64 encoded string
   * @param {string} mimeType - MIME type of the file
   * @returns {Blob}
   */
  base64ToBlob(base64, mimeType = 'application/octet-stream') {
    const arrayBuffer = this.base64ToArrayBuffer(base64);
    return new Blob([arrayBuffer], { type: mimeType });
  }

  /**
   * Helper: Get file as downloadable URL (for binary files)
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<string|null>} - Returns blob URL or null
   */
  async getFileAsUrl(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      if (!file) return null;

      if (file.isBinary && file.content) {
        // Check if content is base64-encoded or raw binary
        // file.encoding will be 'base64' if stored correctly from GitLab
        const isBase64 = file.encoding === 'base64' || /^[A-Za-z0-9+/=]+$/.test(file.content.substring(0, 100));
        
        // eslint-disable-next-line no-console
        console.log('🔧 Creating blob from:', {
          filePath,
          contentLength: file.content.length,
          encoding: file.encoding,
          isBase64,
          mimeType: file.mimeType,
          firstChars: file.content.substring(0, 30)
        });
        
        let blob;
        if (isBase64) {
          // Content is base64-encoded - this is the correct way
          // eslint-disable-next-line no-console
          console.log('📦 Using base64ToBlob converter');
          blob = this.base64ToBlob(file.content, file.mimeType);
          
          // eslint-disable-next-line no-console
          console.log('✅ Blob created from base64:', {
            size: blob.size,
            type: blob.type,
            base64Length: file.content.length,
            expectedBinarySize: Math.floor(file.content.length * 0.75) // base64 is ~33% larger than binary
          });
        } else {
          // Content is corrupted raw binary string - try to salvage it
          // eslint-disable-next-line no-console
          console.warn('⚠️ Content is corrupted raw binary string - attempting conversion');
          const bytes = new Uint8Array(file.content.length);
          for (let i = 0; i < file.content.length; i++) {
            bytes[i] = file.content.charCodeAt(i) & 0xFF; // Mask to 8 bits
          }
          blob = new Blob([bytes], { type: file.mimeType || 'application/octet-stream' });
          
          // eslint-disable-next-line no-console
          console.log('⚠️ Blob created from corrupted binary:', {size: blob.size, type: blob.type});
        }
        
        return URL.createObjectURL(blob);
      }

      // For text files, create a blob from text content
      const blob = new Blob([file.content], { type: 'text/plain' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error creating file URL:', error); /* eslint-disable-line no-console */
      return null;
    }
  }

  /**
   * Helper: Check if file is binary based on stored metadata
   * @param {string} projectId
   * @param {string} serviceName
   * @param {string} filePath
   * @returns {Promise<boolean>}
   */
  async isBinaryFile(projectId, serviceName, filePath) {
    try {
      const file = await this.getFile(projectId, serviceName, filePath);
      return file?.isBinary || false;
    } catch (error) {
      console.error('Error checking if file is binary:', error); /* eslint-disable-line no-console */
      return false;
    }
  }
}

// Export singleton instance
const indexedDbFileStorage = new IndexedDbFileStorage();

export {GIT_STATE, PREVIEW_STATE, EXCLUDED_FILES_FOR_AI, shouldExcludeFromAI};
export default indexedDbFileStorage;
